import { useDispatch, useSelector } from 'react-redux';
import useLocalStorage from '@hooks/useLocalStorage';
import { constant } from '@config/BaseSetting';
import { getInvoicesListAsync } from '@features/invoice/getInvoicesListSlice';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { showFixedAlert } from '@features/application/applicationSlice';
import { useTranslation } from 'react-i18next';
import useFeatureFlag from '@hooks/useFeatureFlag';
import { MenuItem } from '@mui/material';
import useEligibleServices from '@hooks/useEligibleServices';

const initialPagination = { pageIndex: 0, pageSize: 10 };

const initialModalState = {
  showModal: false,
  invoice: null,
};

export default function useInvoicesService(externalResellerId) {
  const { t } = useTranslation();
  const { hasPermission, featureFlags } = useFeatureFlag();
  const { isEligible } = useEligibleServices();
  const { getInvoicesListState } = useSelector((state) => state.getInvoicesList);
  const [accessToken] = useLocalStorage(constant.ACCESS_TOKEN_STORAGE);
  const dispatch = useDispatch();
  const [date, setDate] = useState(dayjs().subtract(1, 'month'));
  const [resellerId, setResellerId] = useState('');
  const [searchText, setSearchText] = useState('');
  const [orderBy, setOrderBy] = useState('');
  const [syncedToXero, setSyncedToXero] = useState('');
  const [pagination, setPagination] = useState(initialPagination);
  const [fetchFlag, setFetchFlag] = useState(true);
  const [matchText, setMatchText] = useState('');
  const [modalState, setModalState] = useState(initialModalState);

  const getSearchId = () => {
    if (/^\d+$/.test(searchText)) {
      return parseInt(searchText, 10);
    }
    return 0;
  };

  const getFilterArray = () => {
    return [
      { field: 'filter_syncedToXero', value: syncedToXero },
      { field: 'filter_startPeriod', value: date.startOf('month').format('YYYY-MM-DDTHH:mm:ss') },
      {
        field: 'filter_endPeriod',
        value: date.add(1, 'month').startOf('month').format('YYYY-MM-DDTHH:mm:ss'),
      },
    ];
  };

  const updateMatchText = () => {
    let currentMatchText = '';
    let first = true;
    const searchId = getSearchId();
    if (searchId) {
      currentMatchText += 'Matching';
      first = false;
      currentMatchText += ` ID = ${searchId}`;
    }
    if (searchText) {
      if (first) {
        currentMatchText += 'Matching';
        first = false;
      }
      currentMatchText += ` Partner Name LIKE %${searchText}% Xero ID LIKE %${searchText}%`;
    }
    if (resellerId) {
      if (first) {
        currentMatchText += 'Matching';
        first = false;
      }
      currentMatchText += ` Partner ID = ${resellerId}`;
    }
    if (syncedToXero) {
      if (first) {
        currentMatchText += 'Matching';
        first = false;
      }
      currentMatchText += ` Synced to Xero = ${syncedToXero}`;
    }

    setMatchText(currentMatchText);
  };

  const fetchInvoices = () => {
    updateMatchText();
    dispatch(
      getInvoicesListAsync({
        accessToken,
        reset: true,
        pageNumber: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        fullTextSearch: searchText,
        searchById: getSearchId(),
        resellerId: externalResellerId || resellerId,
        filter: getFilterArray(),
        orderBy: orderBy || 'CreatedAt',
      })
    );
  };

  useEffect(() => {
    if (fetchFlag) {
      if (!getInvoicesListState.fetching) {
        fetchInvoices();
      }
      setFetchFlag(false);
    }
  }, [fetchFlag]);

  const handleChangeDate = setDate;

  const handleChangeSyncedToXero = (e) => setSyncedToXero(e.target.value);

  const handleChangeResellerId = (v) => setResellerId(v);

  const handleChangeSearchText = (e) => setSearchText(e.target.value);

  const handleChangeOrderBy = (e) => setOrderBy(e.target.value);

  const handleChangePagination = (newPagination) => {
    setPagination(newPagination);
    setFetchFlag(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setPagination(initialPagination);
    setFetchFlag(true);
  };

  const handleReset = () => {
    setSyncedToXero('');
    setDate(dayjs().subtract(1, 'month'));
    setResellerId('');
    setSearchText('');
    setOrderBy('');
    setPagination(initialPagination);
    setFetchFlag(true);
  };

  const showBillingAdjustmentsAndDiscountsModal = (row) => {
    if (row.original.isPaid) {
      dispatch(
        showFixedAlert({
          descriptionHtml: t(
            'invoices_page.billing_adjustments_and_discounts.title.validations.is_paid'
          ),
          severity: 'error',
        })
      );
    } else {
      setModalState({
        showModal: true,
        invoice: row.original,
      });
    }
  };

  const menuActions = ({ row, closeMenu }) => {
    let i = 0;
    const items = [];
    if (
      hasPermission(featureFlags.BillingAdjustmentsAndDiscounts) &&
      isEligible(constant.SERVICE_IDS.BILLING_ADJUSTMENTS_AND_DISCOUNTS)
    ) {
      i += 1;
      items.push(
        <MenuItem
          key={i}
          onClick={() => {
            showBillingAdjustmentsAndDiscountsModal(row);
            setTimeout(() => {
              closeMenu();
            }, 1000);
          }}>
          {t('invoices_page.billing_adjustments_and_discounts.title')}
        </MenuItem>
      );
    }
    return items;
  };

  const hasMenuActions =
    hasPermission(featureFlags.BillingAdjustmentsAndDiscounts) &&
    isEligible(constant.SERVICE_IDS.BILLING_ADJUSTMENTS_AND_DISCOUNTS);

  const handleCloseModal = () => {
    setModalState(initialModalState);
    setFetchFlag(true);
  };

  return {
    date,
    handleChangeDate,
    resellerId,
    handleChangeResellerId,
    syncedToXero,
    handleChangeSyncedToXero,
    searchText,
    handleChangeSearchText,
    orderBy,
    handleChangeOrderBy,
    pagination,
    handleChangePagination,
    handleSubmit,
    handleReset,
    matchText,
    listState: getInvoicesListState,
    menuActions,
    hasMenuActions,
    handleCloseModal,
    modalState,
  };
}
